import { useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import PropTypes from "prop-types";

import { ToastContext } from "common/contexts";
import getGAUserID from "common/fetch/getGAUserID";
import sendData from "common/fetch/post";
import trigger from "common/trigger";

import ButtonNew from "components/atoms/ButtonNew/index";
import { BiLoaderAlt } from "components/atoms/Icon/bi/BiLoaderAlt";
import Img from "components/atoms/Img";
import { CheckBox, InputEmail, InputText } from "components/atoms/Input/index";
import ReCaptcha from "components/atoms/Recaptcha/index";
import Title from "components/atoms/Title";
import Toast from "components/atoms/Toast";

/** ========== */
const Form = ({
  triggerName,
  contactFromId,
  apiLink,
  checkedId,
  unCheckId,
  checkboxText,
  checkboxTextClassName,
  countryId,
}) => {
  /* Variables Define */

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm();

  const { openAlert, setOpenAlert } = useContext(ToastContext);
  const recaptchaRef = useRef();

  useEffect(() => {
    const getHash = window.location.hash || "";

    if (getHash.length > 0) {
      document.getElementById(getHash.replace("#", ""))?.click();
    }
  }, []);

  const handlingSuccess = () => {
    reset();
    clearErrors();
  };

  const onSubmit = async (data) => {
    const token = await recaptchaRef.current.executeAsync();

    if (token) {
      const GAUserID = getGAUserID();
      const newData = { ...data };

      newData.client_id = GAUserID ? GAUserID : "";
      newData.website_id = process.env.NEXT_PUBLIC_WEBSITE_ID;
      if (countryId) {
        newData.company_country_id = countryId;
      }
      newData.contact_from_id =
        checkboxText?.trim().length > 0
          ? data.contactFromId
            ? checkedId
            : unCheckId
          : contactFromId;

      sendData({
        api: `${process.env.NEXT_PUBLIC_ORDER_API_ENDPOINT}/${apiLink}`,
        data: newData,
        token,
      })
        .then((res) => {
          if (res.state) {
            trigger("contact_form", data, triggerName);
            clearErrors();
            openAlert(51000, "custom", {
              element: (
                <>
                  {/* <ProgressBar timer={timer} color={`bg-bbc-green-500`} /> */}
                  <div className="">
                    <Img
                      src="/assets/icons/checked.png"
                      width={90}
                      height={92}
                      alt="icon success"
                      className="mb-6"
                    />
                  </div>
                  <p className="mb-6 text-2xl font-semibold text-bbc-brand-800">
                    You&#39;re all set!
                  </p>
                  <p className="text-sm text-bbc-gray">
                    Your e-book will be sent to your inbox shortly. Please check
                    your email to view your download.
                  </p>
                  <ButtonNew
                    onClick={() => {
                      setOpenAlert(false);
                    }}
                    className="relative mt-4 inline-flex  items-center justify-center rounded-full bg-bbc-brand-500 p-2 text-base text-white hover:bg-bbc-secondary-100 "
                    style={{ width: "160px" }}
                  >
                    OK
                  </ButtonNew>
                </>
              ),
            });
            handlingSuccess();
          } else {
            openAlert(3000, "error");
            console.log("error");
          }
        })
        .catch((e) => {
          openAlert(3000, "error");
          console.log(e, "error");
        });
    }
  };
  const inputClass = "rounded-[49px] border-none w-full py-2 px-4 text-base";

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="mb-4 lg:col-6">
            <InputText
              register={register}
              errors={errors}
              name="first_name"
              placeholder="First Name"
              requiredText="First Name is required"
              className={inputClass}
            />
          </div>
          <div className="mb-4 lg:col-6">
            <InputText
              register={register}
              errors={errors}
              name="last_name"
              placeholder="Last Name"
              requiredText="Last Name is required"
              className={inputClass}
            />
          </div>
        </div>
        <div className="mb-4">
          <InputEmail
            register={register}
            errors={errors}
            className={inputClass}
          />
        </div>
        {checkboxText?.trim().length > 0 && (
          <div className="mb-4">
            <CheckBox
              register={register}
              errors={errors}
              name="contactFromId"
              text={checkboxText}
              checkboxTextClassName={checkboxTextClassName}
            />
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-between">
          <div className="col-12 mb-6 text-center lg:col-auto lg:mb-0">
            <div className="inline-block">
              <ReCaptcha ref={recaptchaRef} className="mx-auto w-max" />
            </div>
          </div>
          <div className="col-12 text-center lg:col-auto">
            <ButtonNew
              type="submit"
              subclass=" min-w-[170px] relative mt-6 inline-flex justify-center items-center w-full lg:w-auto"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <BiLoaderAlt
                  className="mr-2 inline-block animate-spin"
                  size={20}
                />
              )}
              Download Now
            </ButtonNew>
          </div>
        </div>
      </form>
    </>
  );
};

const FormDownload = ({
  title,
  description,
  descriptionClass,
  checkboxText,
  checkboxTextClassName,
  triggerName,
  apiLink,
  contactFromId,
  checkedId,
  unCheckId,
  countryId,
}) => {
  return (
    <div className="row">
      <div className="px-10 pb-10 lg:col-6 lg:pb-0">
        <Title
          text={title}
          className="pb-6 text-center text-bbc-30px font-semibold text-bbc-brand-800 lg:text-left lg:text-bbc-40px lg:leading-[60px]"
        />

        <p
          className={`${
            descriptionClass
              ? descriptionClass
              : "text-center text-base text-bbc-gray lg:text-left"
          }`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
      <div className="lg:col-6">
        <Toast>
          <Form
            checkboxText={checkboxText}
            checkboxTextClassName={checkboxTextClassName}
            triggerName={triggerName}
            contactFromId={contactFromId}
            apiLink={apiLink}
            checkedId={checkedId}
            unCheckId={unCheckId}
            countryId={countryId}
          />
        </Toast>
      </div>
    </div>
  );
};

export default FormDownload;

FormDownload.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  nameProps: PropTypes.object,
  emailProps: PropTypes.object,
  messageProps: PropTypes.object,
  triggerName: PropTypes.string,
  contactFromId: PropTypes.number,
  apiLink: PropTypes.string,
  haveCheckBox: PropTypes.any,
  checkedId: PropTypes.any,
  unCheckId: PropTypes.any,
  checkboxText: PropTypes.string,
  checkboxTextClassName: PropTypes.string,
};

FormDownload.defaultProps = {
  title: "",
  description: "",
  nameProps: {},
  emailProps: {},
  messageProps: {},
  checkboxTextClassName: "",
};

/** ========== */
